import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
    forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as ace from 'ace-builds';
import 'ace-builds/src-noconflict/ace';
import 'ace-builds/src-noconflict/mode-text';
import 'ace-builds/src-noconflict/theme-textmate';
import 'ace-builds/src-noconflict/theme-dracula';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { PYST_ACE_MODE, registerPystAceMode } from './pyst-ace-mode';
import { formatPystQuery } from './pyst-format';
import { PystService } from './pyst.service';

/**
 * A pyst query editor: Ace with the pyst highlight mode, an optional Format action,
 * and server-side linting through pyst's /parse — the same pieces the monitoring
 * app's editor uses, wired as a form control so it drops in where a textarea was.
 *
 * Linting is whole-document: /parse reports success or a message with no position,
 * so the message is shown beneath the editor rather than pretending to know a line.
 */
@Component({
    selector: 'app-pyst-editor',
    templateUrl: './pyst-editor.component.html',
    styleUrls: ['./pyst-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PystEditorComponent),
            multi: true,
        },
    ],
})
export class PystEditorComponent implements AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {
    @ViewChild('editor') private editorHost: ElementRef<HTMLElement>;

    @Input() readOnly = false;
    /** Ace theme name; 'textmate' is light, 'dracula' dark. */
    @Input() theme: 'textmate' | 'dracula' = 'textmate';
    @Input() height = '240px';
    /** Ask pyst to parse the query as it changes. */
    @Input() lint = true;
    /** Show the Format action. */
    @Input() allowFormat = true;

    @Output() lintResult = new EventEmitter<string | null>();

    public error: string | null = null;

    private aceEditor: ace.Ace.Editor | null = null;
    private pendingValue: string | null = null;
    private onChange: (value: string) => void = () => undefined;
    private onTouched: () => void = () => undefined;
    private changes = new Subject<string>();
    private lintSubscription?: Subscription;
    private resizeObserver?: ResizeObserver;
    /** Suppress the change event fired by our own setValue. */
    private settingValue = false;

    constructor(private pyst: PystService) {}

    ngAfterViewInit(): void {
        registerPystAceMode(ace);
        const editor = ace.edit(this.editorHost.nativeElement);
        this.aceEditor = editor;
        editor.setTheme(`ace/theme/${this.theme}`);
        editor.session.setMode(PYST_ACE_MODE);
        editor.session.setUseWrapMode(true);
        editor.setReadOnly(this.readOnly);
        editor.setOptions({
            showPrintMargin: false,
            highlightActiveLine: !this.readOnly,
            highlightGutterLine: !this.readOnly,
            fontSize: 12,
            tabSize: 2,
            useSoftTabs: true,
        });
        if (this.readOnly) {
            editor.renderer.setShowGutter(false);
        }

        if (this.pendingValue !== null) {
            this.setEditorValue(this.pendingValue);
            this.pendingValue = null;
        }

        editor.on('change', () => {
            if (this.settingValue) {
                return;
            }
            const value = editor.getValue();
            this.onChange(value);
            this.changes.next(value);
        });
        editor.on('blur', () => this.onTouched());

        this.lintSubscription = this.changes.pipe(debounceTime(400)).subscribe((value) => {
            void this.runLint(value);
        });

        // Ace only relayouts on window resize; the card's frame is user-resizable.
        this.resizeObserver = new ResizeObserver(() => editor.resize());
        this.resizeObserver.observe(this.editorHost.nativeElement);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.aceEditor) {
            return;
        }
        if (changes['readOnly']) {
            this.aceEditor.setReadOnly(this.readOnly);
        }
        if (changes['theme']) {
            this.aceEditor.setTheme(`ace/theme/${this.theme}`);
        }
    }

    ngOnDestroy(): void {
        this.lintSubscription?.unsubscribe();
        this.resizeObserver?.disconnect();
        this.aceEditor?.destroy();
    }

    /** Pretty-print in place; whitespace-only, so the query's meaning is unchanged. */
    format(): void {
        if (!this.aceEditor) {
            return;
        }
        const formatted = formatPystQuery(this.aceEditor.getValue());
        this.setEditorValue(formatted);
        this.onChange(formatted);
        this.changes.next(formatted);
    }

    writeValue(value: string | null): void {
        const text = value ?? '';
        if (this.aceEditor) {
            this.setEditorValue(text);
        } else {
            this.pendingValue = text;
        }
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.aceEditor?.setReadOnly(isDisabled || this.readOnly);
    }

    private setEditorValue(text: string): void {
        if (!this.aceEditor) {
            return;
        }
        this.settingValue = true;
        try {
            // -1 keeps the cursor at the start instead of selecting everything.
            this.aceEditor.setValue(text, -1);
        } finally {
            this.settingValue = false;
        }
    }

    private async runLint(value: string): Promise<void> {
        if (!this.lint) {
            return;
        }
        const trimmed = value.trim();
        // /parse splits on `?` and parses the query part; a macro path has neither,
        // and its arguments are validated by the macro at request time instead.
        if (!trimmed || /^\/?~/.test(trimmed)) {
            this.setError(null);
            return;
        }
        try {
            const result = await this.pyst.parse(trimmed);
            this.setError(result.success ? null : result.message || 'Syntax error');
        } catch (error) {
            // Lint is advisory; a failed round-trip must not look like a bad query.
            console.warn('pyst /parse unavailable', error);
            this.setError(null);
        }
    }

    private setError(message: string | null): void {
        this.error = message;
        this.lintResult.emit(message);
        const session = this.aceEditor?.session;
        if (!session) {
            return;
        }
        if (message) {
            session.setAnnotations([{ row: 0, column: 0, text: message, type: 'error' }]);
        } else {
            session.clearAnnotations();
        }
    }
}
